import { PricingFunction } from './types';
import { getErrorReporter } from '../../utils/errors';

/**
 * Prices a flat rate ad in cents
 * Assigns the flat rate to the first run only, unless a dayRate is specified
 * Will select the highest non-zero rate defined if the number of runs does not match a non-zero rate
 */
export const priceFlat: PricingFunction = options => {
  const { dayRate, runNumber, numRuns, rateRecord } = options;

  if (dayRate) {
    return dayRate;
  }
  if (runNumber > 0) {
    return 0;
  }

  // Build a single array of rates by run number: index 0 -> rate_0, 1 -> rate_1, 2 -> rate_2, etc.
  const ratePricesInCents: number[] = [];
  ratePricesInCents[0] = rateRecord.rate_0 || 0;
  ratePricesInCents[1] = rateRecord.rate_1 || 0;
  ratePricesInCents[2] = rateRecord.rate_2 || 0;
  if (rateRecord.additionalRates) {
    for (const key of Object.keys(rateRecord.additionalRates)) {
      const n = parseInt(key.replace('rate_', ''), 10);
      if (Number.isFinite(n) && n >= 0) {
        const value = rateRecord.additionalRates[key] || 0;
        ratePricesInCents[n] = value;
      }
    }
  }

  // Lookup: numRuns maps to index (numRuns - 1). If matched rate is non-zero, return it.
  const index = Math.max(0, numRuns - 1);
  const matched = ratePricesInCents[index];
  if (typeof matched === 'number' && matched > 0) {
    return matched;
  }

  // Fallback: highest indexed non-zero rate defined using filter + last element.
  const nonZero = ratePricesInCents.filter(v => typeof v === 'number' && v > 0);
  if (nonZero.length > 0) {
    // Logging: flag if there are zero-value rates at the beginning/middle before the last non-zero.
    const lastFilteredIndex = nonZero.length - 1;
    const fallbackValue = nonZero[lastFilteredIndex];
    const lastNonZeroIndex = ratePricesInCents.lastIndexOf(fallbackValue);
    if (lastNonZeroIndex !== lastFilteredIndex) {
      // If indexes don't match, that means we removed a zero-value rate before the last non-zero.
      getErrorReporter().logAndCaptureWarning(
        'Zero-value indices detected in beginning or middle of flat rate; falling back to highest indexed non-zero rate'
      );
    }

    return fallbackValue;
  }

  getErrorReporter().logAndCaptureWarning(
    'No non-zero rates defined for flat rate; defaulting to 0 cents'
  );
  // If none are non-zero, default to 0.
  return 0;
};
